import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowDown, ArrowRight, Plus, Clock } from 'lucide-react';
import { usePage } from '../contexts/PageContext';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import RichText from '../components/RichText';
import api from '../lib/api';

export default function HomePage() {
  const { t, seo } = usePage('home');
  return (
    <>
      <SEO title={seo.title} description={seo.description} keywords={seo.keywords} image={seo.og_image} />
      <Hero t={t} />
      <About t={t} />
      <Business t={t} />
      <Tactics t={t} />
      <Slider t={t} />
      <Partners t={t} />
      <CTAStrip t={t} />
      <Blog t={t} />
      <FinalCTA t={t} />
    </>
  );
}

function Hero({ t }) {
  return (
    <section className="relative overflow-hidden">
      <div className="container-x relative pt-20 pb-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 relative z-10">
            <h1 className="heading-display text-[40px] sm:text-[56px] lg:text-[72px] xl:text-[80px]">
              {t('hero_title', 'The digital sales engine that turns banks into autonomous sellers')}
            </h1>
            <Link to="/contact" className="btn-outline mt-12">
              <span>{t('hero_cta_primary', "Let's connect")}</span>
              <span className="arrow"><ArrowUpRight size={14} /></span>
            </Link>
          </div>
          <div className="lg:col-span-5 lg:pb-32 relative z-10">
            <p className="text-[15px] leading-relaxed text-[#C6C4CF] max-w-sm ml-auto text-right">
              <RichText text={t('hero_description', '')} />
            </p>
          </div>
        </div>
      </div>

      {/* Hero image with watermark */}
      <div className="relative -mt-10 lg:-mt-32">
        <div className="container-x relative">
          <div className="relative h-[680px] lg:h-[820px]">
            {/* Massive watermark text */}
            <div className="absolute inset-x-0 bottom-0 flex items-end justify-center pointer-events-none select-none">
              <span className="watermark whitespace-nowrap">ClicksyHub</span>
            </div>
            {/* Image positioned right */}
            <div className="absolute right-0 bottom-0 h-full w-[58%] lg:w-[52%]">
              <img src="/images/hero-banner-1.webp" alt="" className="w-full h-full object-cover object-center" loading="eager" style={{ maskImage: 'linear-gradient(180deg, transparent 0%, #000 12%, #000 100%)', WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, #000 12%, #000 100%)' }} />
            </div>
            {/* Scroll indicator */}
            <a href="#about" className="absolute left-2 lg:left-8 bottom-10 w-12 h-12 rounded-full border border-[#C6C4CF] text-[#C6C4CF] hover:bg-[#F5F137] hover:text-[#232323] transition-colors flex items-center justify-center z-20"><ArrowDown size={16} /></a>
          </div>
        </div>
      </div>
    </section>
  );
}

function About({ t }) {
  return (
    <section id="about" className="py-24 lg:py-36">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow">{t('about_eyebrow', 'about ClicksyHub')}</span>
          </div>
          <div className="lg:col-span-7">
            <h2 className="heading-display text-[36px] sm:text-[48px] lg:text-[64px]">
              {t('about_title', 'Empowering Financial Institutions To Achieve Goals With Strategic Digital Solutions')}
            </h2>
            <p className="mt-8 text-[16px] leading-relaxed text-[#C6C4CF] max-w-2xl">
              <RichText text={t('about_description', '')} />
            </p>
          </div>
        </div>

        {/* Image + stats overlapping grid */}
        <div className="mt-16 lg:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden bg-[#2A2A2A]">
              <img src="/images/about-1.webp" alt="" className="w-full h-full object-cover grayscale-[20%]" loading="lazy" />
            </div>
          </div>
          <div className="lg:col-span-3 lg:pb-16">
            <div className="flex items-baseline">
              <p className="giant-num text-[140px] lg:text-[200px] text-[#FAF6EF]">{t('stat_4_value', '12')}</p>
              <span className="giant-num text-[80px] lg:text-[120px] text-[#C6C4CF]">{t('stat_4_suffix', '+')}</span>
            </div>
            <p className="text-xs uppercase tracking-[0.28em] text-[#C6C4CF] font-medium mt-2">{t('stat_4_label', 'Years of fintech expertise')}</p>
          </div>
          <div className="lg:col-span-4">
            <div className="aspect-[4/3] overflow-hidden bg-[#2A2A2A]">
              <img src="/images/about-2.webp" alt="" className="w-full h-full object-cover grayscale-[20%]" loading="lazy" />
            </div>
          </div>
        </div>

        {/* Smaller stats row */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-10 border-t border-[#3A332A] pt-12">
          {[
            { v: t('stat_1_value', '312'), s: t('stat_1_suffix', '%'), l: t('stat_1_label', 'Lift in qualified leads') },
            { v: t('stat_2_value', '8'), s: t('stat_2_suffix', 'x'), l: t('stat_2_label', 'Faster time-to-product') },
            { v: t('stat_3_value', '97'), s: t('stat_3_suffix', '%'), l: t('stat_3_label', 'Customer satisfaction') },
          ].map((s, i) => (
            <div key={i}>
              <div className="flex items-baseline">
                <p className="giant-num text-[64px] lg:text-[88px]">{s.v}</p>
                <span className="giant-num text-[36px] lg:text-[48px] text-[#C6C4CF]">{s.s}</span>
              </div>
              <p className="text-xs uppercase tracking-[0.28em] text-[#C6C4CF] mt-2">{s.l}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Business({ t }) {
  const items = [
    { img: '/images/service-1.webp', title: t('service_1_title', 'Brand & Audience Intelligence'), desc: t('service_1_desc', '') },
    { img: '/images/service-2.webp', title: t('service_2_title', 'Attraction Engine'), desc: t('service_2_desc', '') },
    { img: '/images/service-3.webp', title: t('service_3_title', 'Autonomous Conversion'), desc: t('service_3_desc', '') },
  ];
  return (
    <section className="py-24 lg:py-36">
      <div className="container-x">
        <div className="text-center mb-16">
          <h2 className="heading-display text-[48px] sm:text-[72px] lg:text-[120px] xl:text-[150px] inline-flex items-center flex-wrap justify-center gap-x-8 leading-[0.9]">
            <span>{t('business_title_a', 'Achieve Banking')}</span>
            <Star />
            <span>{t('business_title_b', 'Growth')}</span>
          </h2>
          <p className="mt-8 text-[16px] text-[#C6C4CF] max-w-xl mx-auto"><RichText text={t('business_description', '')} /></p>
        </div>

        <div className="border-t border-[#3A332A]">
          {items.map((it, i) => (
            <Link to="/services" key={i} className="group grid grid-cols-1 lg:grid-cols-12 gap-6 items-center py-8 lg:py-10 border-b border-[#3A332A] hover:bg-[#2A2A2A] transition-colors px-2 lg:px-6">
              <div className="lg:col-span-1 text-[#F5F137] text-sm tracking-[0.28em] font-medium">0{i+2}</div>
              <div className="lg:col-span-3">
                <div className="aspect-[16/9] overflow-hidden bg-[#2A2A2A]">
                  <img src={it.img} alt={it.title} className="w-full h-full object-cover grayscale-[40%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" loading="lazy" />
                </div>
              </div>
              <div className="lg:col-span-6">
                <h3 className="heading-display text-3xl lg:text-5xl text-[#C6C4CF] group-hover:text-[#FAF6EF] transition-colors">{it.title}</h3>
                <p className="mt-3 text-[#8A8275] max-w-2xl text-sm leading-relaxed lg:text-base"><RichText text={it.desc} /></p>
              </div>
              <div className="lg:col-span-2 flex justify-end">
                <span className="btn-icon-only"><ArrowUpRight size={18} /></span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Star() {
  return (
    <span className="inline-flex items-center justify-center align-middle" aria-hidden="true" style={{ fontSize: 'inherit' }}>
      <span className="block rounded-full" style={{ width: '0.7em', height: '0.7em', background: '#FF6220' }} />
    </span>
  );
}

function Tactics({ t }) {
  const list = [
    t('tactic_1', 'Expand to new countries digitally'),
    t('tactic_2', 'Improve customer retention'),
    t('tactic_3', 'Streamline onboarding & operations'),
    t('tactic_4', 'Diversify revenue with bundles'),
    t('tactic_5', 'Optimize pricing per segment'),
  ];
  const [active, setActive] = useState(1);
  return (
    <section className="relative">
      <div className="absolute inset-0 -z-0">
        <img src="/images/tactics.webp" alt="" className="w-full h-full object-cover opacity-40" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#232323] via-[#232323]/80 to-[#232323]" />
      </div>
      <div className="relative container-x py-32 lg:py-44">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 bg-[#232323]/90 backdrop-blur p-6 lg:p-10 rounded-md border border-[#3A332A]">
            <ul>
              {list.map((item, i) => (
                <li
                  key={i}
                  onMouseEnter={() => setActive(i)}
                  className={`group flex items-center justify-between py-5 cursor-pointer border-b border-[#3A332A] last:border-0 transition-colors ${active === i ? '' : ''}`}
                >
                  <span className="flex items-center gap-8">
                    <span className="text-[#F5F137] text-sm tracking-[0.28em] font-medium w-8">{String(i+1).padStart(2, '0')}</span>
                    <span className={`text-xl lg:text-2xl transition-colors ${active === i ? 'text-[#FAF6EF]' : 'text-[#8A8275]'}`} style={{ letterSpacing: '-0.01em' }}>{item}</span>
                  </span>
                  <span className={`flex items-center justify-center w-10 h-10 rounded-full border transition-all ${active === i ? 'border-[#C6C4CF] text-[#C6C4CF]' : 'border-[#3A332A] text-[#8A8275]'}`}>
                    <ArrowRight size={14} />
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5 lg:pl-10">
            <h2 className="heading-display text-[36px] lg:text-[52px]">{t('tactics_title', 'Maximize Your Bank Revenue With These Tactics')}</h2>
            <div className="mt-12 flex items-baseline gap-4">
              <p className="giant-num text-[120px] lg:text-[180px] text-[#FF9AD2]">{t('tactics_growth_value', '57')}</p>
              <span className="giant-num text-[64px] text-[#FF9AD2]">%</span>
            </div>
            <p className="text-xs uppercase tracking-[0.28em] text-[#C6C4CF]">{t('tactics_growth_label', 'Average revenue growth')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Slider({ t }) {
  const items = [
    { title: t('slide_1_title', 'Brand & Market Analysis'), sub: t('slide_1_subtitle', 'Strategy phase'), bg: '#F5F137', dark: true },
    { title: t('slide_2_title', 'Attraction Campaigns'),    sub: t('slide_2_subtitle', 'Acquisition'),     bg: '#FAF6EF', dark: true },
    { title: t('slide_3_title', 'Autonomous Conversion'),    sub: t('slide_3_subtitle', 'Sales engine'),    bg: '#4F5758', dark: false },
  ];
  return (
    <section className="py-24 lg:py-36">
      <div className="container-x">
        <div className="text-center mb-16">
          <span className="eyebrow mx-auto">{t('slider_eyebrow', 'How we work')}</span>
          <h2 className="heading-display text-[36px] sm:text-[52px] lg:text-[72px] mt-6 max-w-4xl mx-auto">
            {t('slider_title', 'Empowering Banks to Achieve Digital Goals With.')}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
          {items.map((it, i) => {
            const fg = it.dark ? '#1A1A1A' : '#FAF6EF';
            const subFg = it.dark ? 'rgba(26,26,26,0.65)' : 'rgba(240,229,211,0.7)';
            const arrowBorder = it.dark ? 'rgba(26,26,26,0.35)' : 'rgba(240,229,211,0.45)';
            return (
              <div
                key={i}
                className="group relative aspect-[8/5] cursor-pointer transition-transform duration-500 hover:-translate-y-1"
                style={{ background: it.bg, color: fg }}
              >
                <div className="absolute inset-0 p-6 lg:p-8 flex flex-col justify-end">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.28em]" style={{ color: subFg }}>{it.sub}</p>
                      <h3 className="mt-2 heading-display text-2xl lg:text-3xl" style={{ color: fg }}>{it.title}</h3>
                    </div>
                    <span
                      className="w-10 h-10 rounded-full border flex items-center justify-center transition-all flex-shrink-0 group-hover:rotate-45"
                      style={{ borderColor: arrowBorder, color: fg }}
                    >
                      <ArrowUpRight size={14} />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Partners({ t }) {
  const ps = ['Zephyr Bank', 'NeoFunds', 'Aurora Trust', 'Heritage Capital', 'Northstar', 'Vela Pay', 'Kairo Bank'];
  return (
    <section className="py-16 border-t border-b border-[#3A332A]">
      <div className="container-x">
        <p className="text-center text-xs uppercase tracking-[0.28em] text-[#C6C4CF] mb-10">{t('partners_title', 'Trusted by digital-first financial institutions')}</p>
        <div className="marquee">
          <div className="marquee-track">
            {[...ps, ...ps].map((p, i) => (
              <span key={i} className="heading-display text-3xl lg:text-4xl text-[#3A332A] hover:text-[#FAF6EF] transition-colors">{p}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTAStrip({ t }) {
  const text = (t('cta_strip', 'Achieve digital growth') || '').split(/·|\|/).map(s => s.trim()).filter(Boolean);
  if (!text.length) text.push('Achieve digital growth', 'Convert more customers');
  return (
    <section className="py-12 bg-[#FAF6EF] text-[#232323]">
      <div className="marquee">
        <div className="marquee-track">
          {[...text, ...text, ...text].map((tx, i) => (
            <span key={i} className="heading-display text-3xl lg:text-5xl flex items-center gap-8 text-[#232323]">
              {tx}
              <Plus size={28} className="text-[#C6C4CF]" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function Blog({ t }) {
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get('/public/articles').then((r) => setItems((r.data || []).slice(0, 3))).catch(() => {});
  }, []);
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return iso; } };
  const tr = (a) => (a.translations?.[lang] || a.translations?.en || {});
  if (!items.length) return null;
  return (
    <section className="py-24 lg:py-36">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-8">
            <span className="eyebrow">{t('blog_eyebrow', 'Latest insights')}</span>
            <h2 className="heading-display text-[36px] sm:text-[52px] lg:text-[64px] mt-6 max-w-3xl">{t('blog_title', 'Browse Our Latest News & Articles')}</h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/blog" className="btn-outline"><span>{t('blog_cta', 'More articles')}</span><span className="arrow"><ArrowUpRight size={14} /></span></Link>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((a) => {
            const tx = tr(a);
            return (
              <Link to={`/blog/${a.slug}`} key={a.slug} className="group block">
                <div className="aspect-[4/3] overflow-hidden bg-[#2A2A2A]">
                  <img src={a.image} alt={tx.title} className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" loading="lazy" />
                </div>
                <div className="pt-5">
                  <div className="flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-[#C6C4CF]">
                    <span>{fmtDate(a.date)}</span>
                    <span className="flex items-center gap-1"><Clock size={12} />{a.reading_minutes} min</span>
                  </div>
                  <h3 className="mt-3 heading-display text-2xl group-hover:text-[#F5F137] transition-colors">{tx.title}</h3>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FinalCTA({ t }) {
  return (
    <section className="py-32">
      <div className="container-x">
        <div className="rounded-md p-10 lg:p-20 grain relative overflow-hidden text-center" style={{ background: '#FBF6EF', color: '#4F5758' }}>
          <Star />
          <h2 className="heading-display text-[36px] sm:text-[52px] lg:text-[80px] max-w-4xl mx-auto mt-6" style={{ color: '#4F5758' }}>
            {t('final_cta_title', 'Ready to turn your bank into an autonomous digital seller?')}
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-[16px]" style={{ color: '#7A8082' }}><RichText text={t('final_cta_desc', '')} /></p>
          <Link to="/contact" className="btn-outline mt-10"><span>{t('final_cta_button', 'Talk to ClicksyHub')}</span><span className="arrow"><ArrowUpRight size={14} /></span></Link>
        </div>
      </div>
    </section>
  );
}
